import {
	ApiFilled,
	CheckOutlined,
	ClockCircleFilled,
	CopyFilled,
	InfoCircleFilled,
} from "@ant-design/icons"
import { AddressLink } from "components/AddressLink/AddressLink"
import { BackButton } from "components/BackButton/BackButton"
import { Button, type ButtonProps } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { Drawer, type DrawerProps } from "components/Drawer/Drawer"
import { Empty } from "components/Empty/Empty"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useDisclosure } from "hooks/useDisclosure"
import { CalendarIcon } from "icons/CalendarIcon"
import { Visit } from "interface/visit"
import { Chat } from "modules/communications/Chat"
import {
	ComponentProps,
	CSSProperties,
	Fragment,
	MouseEventHandler,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useNavigate, useParams } from "react-router"
import { useOwniCalUrl, useOwnVisits } from "services/visits"
import { cn } from "styles/cn"
import { copyToClipboard } from "utils"
import { DEFAULT_VISIT_DURATION } from "utils/constants"
import {
	asDate,
	dateAsDay2Digit,
	dateAsHour,
	dayjs,
	Dayjs,
	formatDate,
	isToday,
	today,
} from "utils/date"
import { prettyVisitInterval } from "utils/visits"

export function MyVisits() {
	const { data = [], isFetching, isFetched } = useOwnVisits()

	return (
		<Spinner spinning={isFetching}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-8 md:gap-12">
				<div className="mt-6 flex w-full flex-col justify-between gap-3 @2xl:flex-row">
					<h2 className="font-nunito text-3xl font-semibold">My visits</h2>

					<CopyiCalUrlButton />
				</div>

				<TodayVisits visits={data} isReady={isFetched} />

				<FutureVisits visits={data} isReady={isFetched} />
			</div>
		</Spinner>
	)
}

function CopyiCalUrlButton() {
	const { data: iCalUrl, isFetching } = useOwniCalUrl()
	const [copySuccess, setCopySuccess] = useState(false)
	const copyHandler = async () => {
		if (copySuccess) return
		await copyToClipboard(iCalUrl!)
		setCopySuccess(true)
		setTimeout(() => {
			setCopySuccess(false)
		}, 1000)
	}
	return (
		<Button
			disabled={!iCalUrl || isFetching}
			loading={isFetching}
			icon={
				copySuccess ? (
					<CheckOutlined
						aria-hidden
						className={cn(copySuccess && "text-mint-100")}
					/>
				) : (
					<CopyFilled aria-hidden />
				)
			}
			onClick={copyHandler}
		>
			{copySuccess
				? "Calendar subscription link copied"
				: "Copy calendar subscription link"}
		</Button>
	)
}

function TodayVisits({
	visits,
	isReady,
}: {
	visits: Visit[]
	isReady: boolean
}) {
	const discloseVisit = useDisclosure<Visit>()
	const { todayDates, firstDate, lastDate, todayVisits } = useMemo(() => {
		const todayVisits = visits.filter(
			({ visitDate }) => visitDate && isToday(visitDate),
		)

		if (todayVisits.length === 0) {
			return { todayDates: new Map<string, Visit[]>() }
		}

		const firstDate = asDate(todayVisits[0].visitDate)
		const lastVisitDate = todayVisits[todayVisits.length - 1]
		const lastDate = asDate(lastVisitDate.visitDate).add(
			lastVisitDate.visitDurationInMinutes ?? DEFAULT_VISIT_DURATION,
			"minute",
		)
		const differenceHours = Math.abs(
			asDate(todayVisits[0].visitDate).diff(lastDate, "hour"),
		)
		const todayHours = Array.from({ length: differenceHours + 2 }, (_, i) =>
			today()
				.hour(i + firstDate.hour())
				.startOf("hour")
				.toISOString(),
		)
		const todayDates = new Map<string, Visit[]>(
			todayHours.map((date) => [date, []]),
		)

		function groupOverlappingIntervals() {
			let currentGroup: Visit[] = []
			let currentGroupEnd = null

			for (const visit of todayVisits) {
				const start = asDate(visit.visitDate)
				const end = start.add(visit.visitDurationInMinutes ?? 0, "minute")

				if (
					// There's no group yet
					!currentGroup.length ||
					// The visit start falls within current group
					(!!currentGroupEnd && start < currentGroupEnd) ||
					// The visit start matches any of the visit start time already in the group
					// (special case grouping because of the way visits under 1 hour (60 minutes) need to be displayed looking like 1 hour)
					currentGroup.some((v) =>
						asDate(v.visitDate).startOf("hour").isSame(start.startOf("hour")),
					)
				) {
					// Event overlaps, include it
					currentGroup.push(visit)
					currentGroupEnd = currentGroupEnd
						? dayjs.max(currentGroupEnd, end)
						: end
				} else {
					// No overlap, push current group and start a new one
					const mappedDateKey = dayjs
						.min(currentGroup.map(({ visitDate }) => asDate(visitDate)))!
						.startOf("hour")
						.toISOString()

					todayDates.set(mappedDateKey, currentGroup)

					currentGroup = [visit]
					currentGroupEnd = end
				}
			}

			if (currentGroup.length) {
				const mappedDateKey = dayjs
					.min(currentGroup.map(({ visitDate }) => asDate(visitDate)))!
					.startOf("hour")
					.toISOString()

				todayDates.set(mappedDateKey, currentGroup)
			}
		}

		groupOverlappingIntervals()

		return {
			todayDates,
			firstDate,
			lastDate,
			todayVisits,
		}
	}, [visits])

	return (
		<div className="flex flex-col gap-3 @4xl:gap-6">
			<h3 className="font-nunito text-2xl font-semibold text-mint-100">
				Today, {formatDate(today().toISOString())}
			</h3>

			<div className="@container/day grid max-h-100 overflow-x-hidden overflow-y-auto rounded-2xl border border-grey-20 py-8 pr-4 pl-6">
				{isReady && todayDates.size === 0 && (
					<Empty Icon={CalendarIcon} description="No visits scheduled today" />
				)}

				<ul
					className="col-start-1 row-start-1 flex flex-col"
					aria-label={
						firstDate && lastDate
							? `Today's schedule spawns from ${dateAsHour(firstDate.toISOString())} to ${dateAsHour(lastDate.toISOString())}`
							: ""
					}
				>
					{[...todayDates.keys()].map((date) => {
						const start = asDate(date)
						const end = start.add(1, "hour")
						const [hour, timeOfDay] = dateAsHour(date).split(" ")

						return (
							<li
								key={`hour-${date}`}
								className="relative flex h-45 min-h-45 gap-3 @5xl/day:h-22 @5xl/day:min-h-22"
								aria-label={`${dateAsHour(date)}`}
							>
								<div className="min-w-12">
									<div className="flex -translate-y-3.5 items-center gap-1">
										<span className="font-headings text-xl font-extrabold">
											{hour}
										</span>
										<span className="text-navy-60">{timeOfDay}</span>
									</div>
								</div>

								<div className="size-full border-t border-t-grey-20" />

								<Now start={start} end={end} />
							</li>
						)
					})}
				</ul>

				<ul
					className="col-start-1 row-start-1 flex flex-col"
					aria-label={
						firstDate && lastDate
							? `${todayVisits.length} visit(s) planned for today, spanning from ${dateAsHour(firstDate.toISOString())} to ${dateAsHour(lastDate.toISOString())}`
							: ""
					}
				>
					{[...todayDates.entries()].map(([date, visits]) => {
						return (
							<li
								key={`visits-at-${date}`}
								className={cn(
									"relative flex h-45 min-h-45 gap-3 @5xl/day:h-22 @5xl/day:min-h-22",
									visits.length === 0 && "invisible",
								)}
								aria-label={`${visits.length} visit(s) starting at ${dateAsHour(date)}`}
							>
								<div className="min-w-12" />

								<div className="@container/hour flex size-full gap-0.5">
									{visits.map((visit) => (
										<TodayVisit
											key={visit.id}
											visit={visit}
											referenceDate={date}
											onClick={discloseVisit.open}
										/>
									))}
								</div>
							</li>
						)
					})}
				</ul>
			</div>

			{discloseVisit.isOpen && !!discloseVisit.value && (
				<VisitDetails
					visit={
						visits.find(({ id }) => id === discloseVisit.value?.id) ??
						discloseVisit.value
					}
					open={discloseVisit.isOpen}
					onClose={discloseVisit.close}
				/>
			)}
		</div>
	)
}

function TodayVisit({
	visit,
	referenceDate,
	onClick,
}: {
	visit: Visit
	referenceDate: string
	onClick: (visit: Visit) => void
}) {
	const { patient, status, visitDate, visitDurationInMinutes } = visit
	const visitInterval = prettyVisitInterval(visit)
	const style = useMemo(() => {
		const heightSpacings = Math.max(4, (visitDurationInMinutes ?? 0) / 15)
		const offsetSpacings = asDate(visitDate).diff(referenceDate, "minute") / 15

		return {
			"--slot-height": `${heightSpacings * 45}px`,
			"--slot-height-5xl": `${heightSpacings * 22}px`,
			"--slot-offset": `${offsetSpacings * 45}px`,
			"--slot-offset-5xl": `${offsetSpacings * 22}px`,
		} as CSSProperties
	}, [referenceDate, visitDate, visitDurationInMinutes])

	if (!patient) {
		return null
	}

	return (
		<article
			tabIndex={0}
			style={style}
			aria-label={`${patient.fullName} visit scheduled between ${visitInterval.replace(" – ", " and ")}`}
			className={cn(
				"@container/visit relative flex w-full gap-2 overflow-hidden bg-mint-0 p-1",
				"translate-y-(--slot-offset) @5xl/day:translate-y-(--slot-offset-5xl)",
				"h-(--slot-height) @5xl/day:h-(--slot-height-5xl)",
				"focus-outline hover:cursor-pointer hover:bg-mint-20",
			)}
			onClick={() => {
				onClick(visit)
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					onClick(visit)
				}
			}}
		>
			<div className="min-h-full min-w-1 rounded-t-full rounded-b-full bg-mint-100" />
			<div className="flex w-full min-w-0 flex-col gap-2 py-1 pr-1 @5xl/day:gap-0">
				<div className="flex w-full flex-col justify-between gap-2 @5xl/day:flex-row @5xl:gap-0">
					<div className="flex max-w-full min-w-0 flex-col">
						<h4 className="text-lg font-semibold whitespace-pre-wrap">
							Visit with {patient.fullName}
						</h4>
						<VisitInterval visit={visit} />
						<AddressLink>{patient.address}</AddressLink>
					</div>

					{status === "COMPLETED" && <VisitCompleted />}

					{status !== "COMPLETED" && patient && (
						<VisitActions
							visit={visit}
							classNames={{
								narrower: "flex gap-1 pr-1 @4xl:hidden @5xl/day:flex-col",
								wider: "hidden gap-1 pr-1 @4xl:flex",
							}}
							buttonProps={{ size: "small", className: "min-w-44" }}
						/>
					)}
				</div>
			</div>
		</article>
	)
}

function Now({ start, end }: { start: Dayjs; end: Dayjs }) {
	const target = useRef<HTMLDivElement>(null)
	const now = asDate()
	const total = end.diff(start, "minute")
	const elapsed = now.diff(start, "minute")
	const percentage = Math.max(0, Math.min((elapsed / total) * 100, 100))

	useLayoutEffect(() => {
		target.current?.scrollIntoView()
	}, [])

	if (!now.isSame(start, "hour")) {
		return null
	}

	return (
		<div
			ref={target}
			className="absolute flex w-full items-center"
			style={{ top: `calc(${percentage}% - 10px)` }}
			aria-label={`It's now ${now.format("hh:mm A")}`}
		>
			<div className="rounded-2xl bg-red-60 px-1 text-white">
				{now.format("hh:mm")}
			</div>
			<div className="w-full border-t border-red-60" />
		</div>
	)
}

function FutureVisits({
	visits,
	isReady,
}: {
	visits: Visit[]
	isReady: boolean
}) {
	const { futureVisits, days } = useMemo(() => {
		const futureVisits = visits.filter(
			({ visitDate }) => visitDate && asDate(visitDate).isAfter(today(), "day"),
		)
		if (futureVisits.length === 0) {
			return { futureVisits: [], days: [] }
		}
		const days = futureVisits.reduce<string[]>((days = [], visit) => {
			const day = asDate(visit.visitDate).startOf("day").toISOString()
			if (!days.includes(day)) {
				days.push(day)
			}
			return days
		}, [])

		return { futureVisits, days }
	}, [visits])

	return (
		<div className="flex flex-col gap-3 @4xl:gap-6">
			<h3 className="font-nunito text-2xl font-semibold text-mint-100">
				Future visits
			</h3>

			<ul
				className="flex max-h-100 flex-col space-y-4 overflow-y-auto rounded-2xl border border-grey-20 py-4 pr-4 pl-6"
				aria-label={
					futureVisits.length > 0
						? `There are ${futureVisits.length} future visits scheduled`
						: "No future visits scheduled"
				}
			>
				{isReady && futureVisits.length === 0 && (
					<Empty Icon={CalendarIcon} description="No future visits scheduled" />
				)}

				{days.map((day) => {
					const dayDate = asDate(day)
					const visits = futureVisits.filter((visit) =>
						asDate(visit.visitDate).isSame(dayDate, "day"),
					)
					const isTomorrow = asDate().add(1, "day").isSame(dayDate, "day")

					return (
						<Fragment key={day}>
							<li
								className="flex flex-col gap-3 pr-4 @4xl:flex-row @4xl:gap-12"
								aria-label={`${visits.length} visit(s) at ${dayDate.format("DD MMM, ddd")}`}
							>
								<div className="flex min-w-26 items-center justify-between gap-3 @4xl:flex-col @4xl:justify-center">
									<div className="flex items-center gap-3 text-nowrap">
										<span className="font-headings text-xl font-extrabold">
											{dateAsDay2Digit(dayDate.toISOString())}
										</span>
										<span className="text-navy-60 uppercase">
											{dayDate.format("MMM, ddd")}
										</span>
									</div>

									{isTomorrow && (
										<div className="rounded-2xl bg-red-60 px-4 text-white @4xl:ml-auto @4xl:w-full">
											Tomorrow
										</div>
									)}
								</div>

								<ul className="flex w-full flex-col gap-3">
									{visits.map((visit) => (
										<div
											key={visit.id}
											className="flex flex-wrap justify-between gap-3 rounded-lg border-2 border-mint-20 p-3 @2xl:flex-nowrap"
										>
											<div className="flex min-w-0 flex-wrap gap-3 font-semibold">
												<div className="flex min-w-0 items-center gap-3 text-nowrap">
													<div className="size-1.5 self-center rounded-full bg-mint-100" />
													{prettyVisitInterval(visit)}
												</div>
												{!isTomorrow && (
													<h4 className="flex min-w-0 items-center gap-3 text-lg">
														{visit.visitType}
														<Tooltip title="Patient's name and location will be displayed the day before visit.">
															<InfoCircleFilled aria-hidden />
														</Tooltip>
													</h4>
												)}
												{isTomorrow && (
													<div className="flex min-w-0 flex-wrap items-center gap-3">
														<h4 className="text-lg wrap-break-word">
															{visit.visitType} with {visit.patient?.fullName}
														</h4>

														<AddressLink>{visit.patient?.address}</AddressLink>
													</div>
												)}
											</div>

											<Wrap
												condition={!isTomorrow}
												with={(children) => (
													<Tooltip title="Messaging will be available the day before the visit.">
														{children}
													</Tooltip>
												)}
											>
												<Chat.CallToAction
													view
													send
													disabled={!isTomorrow}
													className="w-full max-w-44 self-start"
													patient={visit.patient!}
													unreadMessagesCount={visit.unreadMessagesCount}
												/>
											</Wrap>
										</div>
									))}
								</ul>
							</li>

							<Divider className="last:hidden" />
						</Fragment>
					)
				})}
			</ul>
		</div>
	)
}

function VisitCompleted() {
	return (
		<div className="flex max-h-fit items-center gap-2">
			<div className="size-1.5 rounded-full bg-mint-100" />
			Completed
		</div>
	)
}

function VisitInterval({ visit }: { visit: Visit }) {
	const visitInterval = prettyVisitInterval(visit)

	return (
		<div className="flex items-center gap-2">
			<ClockCircleFilled aria-hidden className="py-1" />
			<span className="truncate">{visitInterval}</span>
		</div>
	)
}

function VisitActions({
	visit,
	classNames,
	buttonProps,
	...props
}: {
	visit: Visit
	classNames: { narrower?: string; wider: string }
	buttonProps?: ButtonProps
} & ComponentProps<"div">) {
	const { scopeId } = useParams()
	const navigate = useNavigate()
	const handleNavigateToInstallation: MouseEventHandler = (e) => {
		e.stopPropagation()
		navigate(`/${scopeId}/visits/${visit.id}`)
	}

	if (!visit.patient) {
		return null
	}

	return (
		<>
			{classNames.narrower && (
				<div {...props} className={classNames.narrower}>
					<Tooltip title={`Start visit with ${visit.patient.fullName}`}>
						<Button
							shape="circle"
							type="primary"
							icon={<ApiFilled aria-hidden />}
							aria-label="Start visit"
							onKeyDown={(e) => e.stopPropagation()}
							onClick={handleNavigateToInstallation}
						/>
					</Tooltip>

					<Chat.CallToAction
						view
						send
						onlyIcon
						patient={visit.patient}
						unreadMessagesCount={visit.unreadMessagesCount}
					/>
				</div>
			)}

			<div {...props} className={classNames.wider}>
				<Button
					{...buttonProps}
					type="primary"
					className={buttonProps?.className ?? "w-full min-w-1/2"}
					icon={<ApiFilled aria-hidden />}
					onKeyDown={(e) => e.stopPropagation()}
					onClick={handleNavigateToInstallation}
				>
					Start visit
				</Button>

				<Chat.CallToAction
					{...buttonProps}
					view
					send
					patient={visit.patient}
					unreadMessagesCount={visit.unreadMessagesCount}
					className={buttonProps?.className ?? "w-full min-w-1/2"}
				/>
			</div>
		</>
	)
}

function VisitDetails({
	open,
	visit,
	onClose,
}: { visit: Visit } & Pick<DrawerProps, "open" | "onClose">) {
	const { id, notes, patient, status } = visit
	const headerId = `visit-details-${id}`

	if (!patient) {
		return null
	}

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={<BackButton onClick={onClose}>My visits</BackButton>}
			aria-labelledby={headerId}
		>
			<h2 id={headerId} className="font-headings text-3xl font-semibold">
				{`Visit with ${patient.fullName}`}
			</h2>

			<div className="flex flex-col gap-4 overflow-y-auto rounded-2xl bg-mint-0 p-2 xs:p-4">
				{status === "COMPLETED" && <VisitCompleted />}

				<VisitInterval visit={visit} />

				<AddressLink>{patient.address}</AddressLink>

				{notes && (
					<p className="rounded-lg border border-blue-20 bg-blue-0 px-3 py-2 whitespace-pre-wrap">
						{notes}
					</p>
				)}

				{status !== "COMPLETED" && (
					<VisitActions
						visit={visit}
						classNames={{ wider: "flex w-full gap-1" }}
					/>
				)}
			</div>
		</Drawer>
	)
}
